/** Who reaches what: the roles activities declare, who holds one, and what a permit gates. */

import { useState } from "react";
import type { ActivityRoleAssignment, InvitationCode, Membership } from "@nebulize/sdk";

import {
  Button,
  Field,
  Select,
  Stack,
  StatusBadge,
  Toolbar,
} from "@/components/ui";

import { stamp } from "../../account/lib/present";
import {
  useActivityRoles,
  useAssignRole,
  useInvitations,
  useMembers,
  usePermits,
  useRevokeRoleAssignment,
} from "../../activities/lib/queries";
import { Chip } from "../../components/Chips";
import { GatesEditor } from "../../components/GatesEditor";
import { Paged } from "../../components/Paged";
import { Mono, Muted } from "../../components/Text";
import { canvasesOf, rolesOf } from "../../lib/catalog";
import type { SpaceActivities } from "../../spaces/lib/queries";
import type { AddedActivityRow, SpaceRow } from "../../types";

import { Loaded, Nothing, Section } from "./Section";

/** Every role one added activity declares, and which of its canvases reach every role. */
function DeclaredRoles({
  row,
  activities,
}: {
  readonly row: AddedActivityRow;
  readonly activities: SpaceActivities;
}) {
  const entry = activities.entryFor(row);
  if (entry === undefined) {
    return (
      <div className="cx-row">
        <div className="cx-row-id">
          <div>
            <div className="cx-node-name">{row.name}</div>
            <Muted small>no manifest for this version</Muted>
          </div>
        </div>
      </div>
    );
  }

  const declared = rolesOf(entry);
  const open = canvasesOf(entry).filter((one) => (one.roles ?? []).length === 0);

  return (
    <div className="cx-row">
      <div className="cx-row-id">
        <div>
          <div className="cx-node-name">{row.name}</div>
          <Stack gap={1}>
            {declared.length === 0 ? (
              <Muted small>declares no role</Muted>
            ) : (
              <div className="cx-chips">
                {declared.map((role) => (
                  <Chip key={role}>{role}</Chip>
                ))}
              </div>
            )}
            {open.map((canvas) => (
              <Muted key={canvas.kind} small>
                {`${canvas.kind} reaches every role`}
              </Muted>
            ))}
          </Stack>
        </div>
      </div>
    </div>
  );
}

/** One added activity's assignments: the catch-all, and everyone named individually. */
function Assignments({
  row,
  activities,
  spaceId,
}: {
  readonly row: AddedActivityRow;
  readonly activities: SpaceActivities;
  readonly spaceId: string;
}) {
  const entry = activities.entryFor(row);
  const declared = entry === undefined ? [] : rolesOf(entry);
  const assignments = useActivityRoles(row.added_activity_id);
  const assign = useAssignRole(row.added_activity_id);
  const revoke = useRevokeRoleAssignment(row.added_activity_id);
  const members = useMembers(spaceId);

  const [role, setRole] = useState(declared[0] ?? "");
  const [holder, setHolder] = useState("");

  if (declared.length === 0) return null;

  const chosen = role === "" ? (declared[0] ?? "") : role;
  const held = assignments.data ?? [];
  const catchAll = held.find((one) => one.role === chosen && one.catch_all === true);
  const named = held.filter((one) => one.role === chosen && one.catch_all !== true);

  return (
    <Stack gap={3}>
      <div className="cx-node-name">{row.name}</div>

      <Field label="Role">
        <Select
          value={chosen}
          options={declared.map((one) => ({ value: one, label: one }))}
          onChange={(event) => {
            setRole(event.target.value);
          }}
        />
      </Field>

      <div className="cx-seg" role="group" aria-label={`Who holds ${chosen} on ${row.name}`}>
        <button
          type="button"
          className={catchAll === undefined ? "cx-seg-btn" : "cx-seg-btn cx-seg-btn-on"}
          onClick={() => {
            if (catchAll === undefined) assign.mutate({ role: chosen, catch_all: true });
          }}
        >
          anyone in the space
        </button>
        <button
          type="button"
          className={catchAll === undefined ? "cx-seg-btn cx-seg-btn-on" : "cx-seg-btn"}
          onClick={() => {
            if (catchAll !== undefined) revoke.mutate(catchAll.assignment_id);
          }}
        >
          only named people
        </button>
      </div>

      <Loaded query={assignments} rows={2}>
        {() =>
          named.length === 0 ? (
            <Muted small>nobody named</Muted>
          ) : (
            <>
              {named.map((one: ActivityRoleAssignment) => (
                <div className="cx-row" key={one.assignment_id}>
                  <div className="cx-row-id">
                    <div>
                      <div className="cx-node-name">{one.account_id ?? ""}</div>
                      <Muted small>{stamp(one.assigned_at)}</Muted>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Remove ${one.account_id ?? ""} from ${one.role}`}
                    loading={revoke.isPending}
                    onClick={() => {
                      revoke.mutate(one.assignment_id);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </>
          )
        }
      </Loaded>

      <Field label="Add a person">
        <Select
          value={holder}
          options={[
            { value: "", label: "pick a member" },
            ...(members.data ?? []).map((one) => ({ value: one.account_id, label: one.account_id })),
          ]}
          onChange={(event) => {
            setHolder(event.target.value);
          }}
        />
      </Field>
      {assign.isError ? <StatusBadge tone="danger">{assign.error.message}</StatusBadge> : null}
      {revoke.isError ? <StatusBadge tone="danger">{revoke.error.message}</StatusBadge> : null}
      <Toolbar>
        <Button
          size="sm"
          variant="secondary"
          loading={assign.isPending}
          disabled={holder === "" || chosen === ""}
          onClick={() => {
            assign.mutate(
              { role: chosen, account_id: holder },
              {
                onSuccess: () => {
                  setHolder("");
                },
              },
            );
          }}
        >
          Assign
        </Button>
      </Toolbar>
    </Stack>
  );
}

function matchMember(row: Membership, needle: string): boolean {
  return `${row.account_id} ${row.role ?? ""}`.toLowerCase().includes(needle);
}

export interface AccessPanelProps {
  readonly space: SpaceRow;
  readonly activities: SpaceActivities;
  /** When one activity is selected, assignments and gates narrow to it. */
  readonly selected: AddedActivityRow | null;
}

export function AccessPanel({ space, activities, selected }: AccessPanelProps) {
  const spaceId = space.space.space_id;
  const members = useMembers(spaceId);
  const invitations = useInvitations(spaceId);
  const permits = usePermits(spaceId);

  const narrowed = selected === null ? activities.rows : [selected];
  const nameOf = (addedActivityId: string): string =>
    activities.rows.find((one) => one.added_activity_id === addedActivityId)?.name ??
    addedActivityId;

  const assignable = narrowed.filter((row) => {
    const entry = activities.entryFor(row);
    return entry !== undefined && rolesOf(entry).length > 0;
  });

  return (
    <Stack gap={5}>
      <Section title="Roles">
        {activities.rows.length === 0 ? (
          <Nothing headline="Nothing added" />
        ) : (
          <Stack gap={2}>
            {activities.rows.map((row) => (
              <DeclaredRoles key={row.added_activity_id} row={row} activities={activities} />
            ))}
          </Stack>
        )}
      </Section>

      <Section
        title="Assignments"
        {...(selected === null ? {} : { note: `narrowed to ${selected.name}` })}
      >
        {narrowed.length === 0 ? (
          <Nothing headline="Nothing added" />
        ) : assignable.length === 0 ? (
          <Nothing headline="No roles declared" />
        ) : (
          <Stack gap={5}>
            {assignable.map((row) => (
              <Assignments
                key={row.added_activity_id}
                row={row}
                activities={activities}
                spaceId={spaceId}
              />
            ))}
          </Stack>
        )}
      </Section>

      <Section title="Members">
        <Loaded query={members} rows={3}>
          {(rows) =>
            rows.length === 0 ? (
              <Nothing headline="Nobody yet" />
            ) : (
              <Paged rows={rows} pageSize={6} placeholder="Find a member" match={matchMember}>
                {(page) => (
                  <Stack gap={2}>
                    {page.map((one) => (
                      <div className="cx-row" key={one.account_id}>
                        <div className="cx-row-id">
                          <div>
                            <div className="cx-node-name">
                              <Mono>{one.account_id}</Mono>
                            </div>
                            <Muted small>{stamp(one.joined_at)}</Muted>
                          </div>
                        </div>
                        <StatusBadge tone="info">{one.role ?? "member"}</StatusBadge>
                      </div>
                    ))}
                  </Stack>
                )}
              </Paged>
            )
          }
        </Loaded>
      </Section>

      <Section title="Invitations">
        <Loaded query={invitations} rows={2}>
          {(rows) =>
            rows.length === 0 ? (
              <Nothing headline="None open" />
            ) : (
              <Stack gap={2}>
                {rows.map((one: InvitationCode) => (
                  <div className="cx-row" key={one.invitation_id}>
                    <div className="cx-row-id">
                      <div>
                        <div className="cx-node-name">{nameOf(one.added_activity_id)}</div>
                        <Muted small>
                          <Mono>{`${one.requirement_name} ${String(one.position)}`}</Mono>
                        </Muted>
                      </div>
                    </div>
                    {one.status === "claimed" ? (
                      <StatusBadge tone="success">claimed</StatusBadge>
                    ) : (
                      <StatusBadge tone="warning">{`open until ${stamp(one.expires_at)}`}</StatusBadge>
                    )}
                  </div>
                ))}
              </Stack>
            )
          }
        </Loaded>
      </Section>

      <Section title="Permits">
        <Loaded query={permits} rows={2}>
          {(rows) =>
            rows.length === 0 ? (
              <Nothing headline="No permit" />
            ) : (
              <Stack gap={2}>
                {rows.map((one) => (
                  <div className="cx-row" key={`${one.name}${one.holder.ref ?? ""}`}>
                    <div className="cx-row-id">
                      <div>
                        <div className="cx-node-name">
                          <Mono>{one.name}</Mono>
                        </div>
                        <Muted small>{`${one.holder.kind} ${one.holder.ref ?? ""}`}</Muted>
                      </div>
                    </div>
                    <Muted small>
                      {one.uses_remaining == null
                        ? "unlimited"
                        : `${String(one.uses_remaining)} left`}
                    </Muted>
                  </div>
                ))}
              </Stack>
            )
          }
        </Loaded>
      </Section>

      <Section
        title="Gates"
        note={
          selected === null
            ? "ordered: the first gate naming a target is the one that fires"
            : `ordered, narrowed to ${selected.name}`
        }
      >
        {narrowed.length === 0 ? (
          <Nothing headline="Nothing added" />
        ) : (
          <Stack gap={5}>
            {narrowed.map((row) => (
              <GatesEditor
                key={row.added_activity_id}
                addedActivityId={row.added_activity_id}
                heading={row.name}
              />
            ))}
          </Stack>
        )}
      </Section>
    </Stack>
  );
}
